import {
    analyzeProductContentApiV1GenerationsOrgIdValidateProductProductProductIdPost,
    getCardValidationDetailApiV1GenerationsOrgIdGenerationGenerationIdCardValidationGet,
    getGenerationExportApiV1GenerationsOrgIdGenerationGenerationIdExportGet,
    getGenerationRoReExportExcelApiV1GenerationsOrgIdGenerationsExportExcelPost,
    getProductGenerationsApiV1GenerationsOrgIdProductProductIdGet,
} from '@/common/api/client/sdk.gen'
import type {
    CardValidationDetailResponse,
    CheckProductCardApiResponseSchema,
    GenerationCreateResponse,
    PageGenerationListResponse,
} from '@/common/api/client/types.gen'
import { useProductFlow, type ProductFlowState } from './productFlow'

const ANALYTICS_FLOW_KEY = 'analytics-card-validation'

export const exportGenerationsToExcel = async (orgId: string, generationIds: string[]): Promise<Blob> => {
    const res = await getGenerationRoReExportExcelApiV1GenerationsOrgIdGenerationsExportExcelPost({
        path: { org_id: orgId },
        body: { generation_ids: generationIds },
        responseType: 'blob',
    })
    return res.data as Blob
}

export const exportGenerationReportBlob = async (
    orgId: string,
    generationId: string,
    regenerate: boolean = false
): Promise<Blob> => {
    const res = await getGenerationExportApiV1GenerationsOrgIdGenerationGenerationIdExportGet({
        path: { org_id: orgId, generation_id: generationId },
        query: { regenerate },
        responseType: 'blob',
    })
    return res.data as Blob
}

export type ProductAnalyticsState = ProductFlowState<
    PageGenerationListResponse,
    CardValidationDetailResponse,
    void,
    GenerationCreateResponse
> & {
    validationResult: CheckProductCardApiResponseSchema | null
    showAnalysisStartCard: boolean
    isSubmitting: boolean
}

export const useProductAnalytics = (): ProductAnalyticsState => {
    const flow = useProductFlow<
        PageGenerationListResponse,
        CardValidationDetailResponse,
        void,
        GenerationCreateResponse
    >({
        flowKey: ANALYTICS_FLOW_KEY,
        fetchList: async ({ orgId, productId, page, pageSize }) => {
            const res = await getProductGenerationsApiV1GenerationsOrgIdProductProductIdGet({
                path: { org_id: orgId, product_id: productId },
                query: {
                    generation_type: 'card_validation',
                    order_by: 'created_at',
                    direction: 'desc',
                    page,
                    size: pageSize,
                },
            })
            return res.data!
        },
        selectIdFromList: list => list?.items[0]?.id ?? null,
        extractIdFromMutationResult: r => r.generation_id,
        fetchDetail: async (orgId, generationId) => {
            const res = await getCardValidationDetailApiV1GenerationsOrgIdGenerationGenerationIdCardValidationGet({
                path: { org_id: orgId, generation_id: generationId },
            })
            return res.data!
        },
        runMutation: async ({ orgId, productId }) => {
            const res = await analyzeProductContentApiV1GenerationsOrgIdValidateProductProductProductIdPost({
                path: { org_id: orgId, product_id: productId },
            })
            return res.data!
        },
    })

    const validationResult = flow.detailQuery.data?.validation_result ?? null
    const hasValidationResult = Boolean(
        flow.detailQuery.data?.status === 'completed' || flow.detailQuery.data?.status === 'failed'
    )

    const passiveDetailBootstrap =
        Boolean(flow.generationId) &&
        !flow.detailQuery.isFetched &&
        !flow.generationMutation.isPending &&
        !flow.detailFormPipelineLoading

    const hideStartCardWhilePassiveLoad =
        flow.isProductLoading || !flow.generationListResolved || passiveDetailBootstrap

    const noEntriesInList = !flow.generationListData?.items?.length

    const showDuringUserDrivenOrIdle =
        flow.generationMutation.isPending || flow.detailFormPipelineLoading || noEntriesInList

    const showAnalysisStartCard = !hasValidationResult && !hideStartCardWhilePassiveLoad && showDuringUserDrivenOrIdle

    const isSubmitting = flow.generationMutation.isPending || flow.detailFormPipelineLoading

    return {
        ...flow,
        validationResult,
        showAnalysisStartCard,
        isSubmitting,
    }
}
